import Auth from "src/services/auth";
import { getBaseUrl } from "src/utils/url";
import request from "src/utils/request";

const getHeaders = () => {
    const tz = (() => {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
            return undefined;
        }
    })();
    return {
        "Content-Type": "application/json",
        ...(Auth.token?.access_token && { authorization: `Bearer ${Auth.token.access_token}` }),
        ...(tz && { tz }),
    };
};

// ─── Conversations ────────────────────────────────────────────────────────────

export const createConversation = () =>
    request("/api/ai/conversations", { method: "POST", body: JSON.stringify({}) });

export const getConversations = () => request("/api/ai/conversations");

export const getConversation = (id) => request(`/api/ai/conversations/${id}`);

export const deleteConversation = (id) =>
    request(`/api/ai/conversations/${id}`, { method: "DELETE" });

export const getLlmUsageMonthSummary = () => request("/api/ai/llm-usage-month");

export const getLlmUsageHistory = () => request("/api/ai/llm-usage-history");

// ─── Streaming Message ────────────────────────────────────────────────────────
// Uses native fetch so we can read the SSE stream with a ReadableStream reader.

export const sendMessage = async (conversationId, text, onEvent, signal) => {
    const response = await fetch(
        `${getBaseUrl()}/api/ai/conversations/${conversationId}/messages`,
        {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ text }),
            signal,
        }
    );

    if (!response.ok) {
        const err = await response.json().catch(() => ({ message: "Request failed" }));
        throw new Error(err.message || "Request failed");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    // eslint-disable-next-line no-constant-condition
    while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() ?? "";

        // eslint-disable-next-line no-restricted-syntax
        for (const block of blocks) {
            const dataLine = block.split("\n").find((l) => l.startsWith("data: "));
            if (dataLine) {
                try {
                    const event = JSON.parse(dataLine.slice(6));
                    onEvent(event);
                } catch {
                    // malformed event, skip
                }
            }
        }
    }
};

// ─── Reports ──────────────────────────────────────────────────────────────────

export const saveReport = (messageId, title, conversationId) =>
    request(`/api/ai/messages/${messageId}/reports`, {
        method: "POST",
        body: JSON.stringify({ title, conversationId }),
    });

export const getReports = () => request("/api/ai/reports");

export const getReport = (id) => request(`/api/ai/reports/${id}`);

export const deleteReport = (id) => request(`/api/ai/reports/${id}`, { method: "DELETE" });

export const shareReport = (id, scope) =>
    request(`/api/ai/reports/${id}/share`, {
        method: "POST",
        body: JSON.stringify({ scope }),
    });

export const refreshReport = async (id, onEvent, signal) => {
    const response = await fetch(`${getBaseUrl()}/api/ai/reports/${id}/refresh`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({}),
        signal,
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ message: "Refresh failed" }));
        throw new Error(err.message || "Refresh failed");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    // eslint-disable-next-line no-constant-condition
    while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() ?? "";

        // eslint-disable-next-line no-restricted-syntax
        for (const block of blocks) {
            const dataLine = block.split("\n").find((l) => l.startsWith("data: "));
            if (dataLine) {
                try {
                    const event = JSON.parse(dataLine.slice(6));
                    onEvent(event);
                } catch {
                    // malformed event, skip
                }
            }
        }
    }
};

export const getSharedReport = (token) => request(`/api/ai/shared/${token}`);

export const getTemplateDashboards = () => request("/api/template/dashboards-list");

export const getAvailableReports = () => request("/api/ai/reports/available");

// ─── Feedback ─────────────────────────────────────────────────────────────────

export const upsertFeedback = (targetType, targetId, value, reason) =>
    request("/api/ai/feedback", {
        method: "POST",
        body: JSON.stringify({ targetType, targetId, value, ...(reason ? { reason } : {}) }),
    });

export const sendReportToDashboard = (savedReportId, reportTitle, dashboardId, teamId, isDynamic) =>
    request("/api/template/add-ai-report", {
        method: "POST",
        body: JSON.stringify({
            savedReportId,
            reportTitle,
            dashboardId,
            ...(teamId && { teamId }),
            ...(isDynamic && { isDynamic: true }),
        }),
    });

export const parametrizeReport = (id) =>
    request(`/api/ai/reports/${id}/parametrize`, { method: "POST" });

export const saveReportSqlTemplate = (id, sqlTemplate, detectedParams) =>
    request(`/api/ai/reports/${id}/sql-template`, {
        method: "PATCH",
        body: JSON.stringify({ sqlTemplate, detectedParams }),
    });
